import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { ContainerQuery } from 'react-container-query';
import { ThemeProvider as MuiThemeProvider, useTheme } from '@mui/material/styles'
import { muiTheme } from 'cityads-v3/utils/styleUtils'

import { FORMAT_CURRENCY } from 'cityads-v3/components/Format/NumberFormat';
import TargetValueFormat from 'cityads-v3/components/Format/TargetValueFormat'

import Card from 'cityads-v3/components/Card/Card';
import Divider from '@mui/material/Divider'
import OfferCardActions from './OfferCardActions';
import OfferCardHeader from './OfferCardHeader';
import OfferCardTag from './OfferCardTag';
import OfferCardTitle from './OfferCardTitle';
import OfferCardSubheader from './OfferCardSubheader';
import OfferCardAvatar from './OfferCardAvatar';
import OfferCardOptions from './OfferCardOptions';
import OfferTargetsTable from './OfferTargetsTable';
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography';

import {
  useOnFavoriteClick,
  usePromoOffers,
  useOfferTags,
} from 'cityads-v3/hooks/offers'

import {
  ACTION_STATUS_WORKING,
} from 'cityads-v3/reducers/offers';

import classes from './OfferCard.scss';
import promoClasses from './PromoOffers.scss';

export const BLACK_FRIDAY_CODE = 'black_friday';

export const offerOptions = (t, { isApi, status, crIsExpected, leadbasedIsExpected }) => [
  {
    title: status === ACTION_STATUS_WORKING ? t('table.date_start') : t('table.date_planned_start'),
    name: status === ACTION_STATUS_WORKING ? 'date_start' : 'planned_start_date',
    format: 'date'
  },
  {
    title: t('table.commission_data'),
    name: 'commission_data',
    format: 'paid',
    precision: 2,
  },
  isApi ? false : {
    title: crIsExpected ? t('table.crExpected') : t('table.cr'),
    name: 'cr',
    format: 'percent',
    precision: 2,
    tooltip: crIsExpected ? t('table.crExpectedTooltip') : t('table.crTooltip'),
    formatter: value => value === null ? null : value * 100
  },
  {
    title: leadbasedIsExpected ? t('table.arExpected') : t('table.ar'),
    name: 'ar',
    format: 'percent',
    precision: 0,
    tooltip: leadbasedIsExpected ? t('table.arExpectedTooltip') : t('table.arTooltip'),
    formatter: value => value !== null && value !== 0 ? value * 100 : null
  },
  isApi ? {
    title: leadbasedIsExpected ? t('table.ecplExpected') : t('table.ecpl'),
    name: 'ecpl',
    tooltip: leadbasedIsExpected ? t('table.ecplExpectedTooltip') : t('table.ecplTooltip'),
    formatter: (value, offerInfo, { ecpl, currency_code, original_ecpl, original_currency_code }) => (
      <TargetValueFormat
        format={FORMAT_CURRENCY}
        currency={currency_code}
        min={ecpl}
        max={ecpl}
        originalCurrency={original_currency_code}
        originalMin={original_ecpl}
        originalMax={original_ecpl}
      />
    ),
  } : false,
  isApi ? false : {
    title: leadbasedIsExpected ? t('table.ecpcExpected') : t('table.ecpc'),
    name: 'ecpc',
    tooltip: leadbasedIsExpected ? t('table.ecpcExpectedTooltip') : t('table.ecpcTooltip'),
    formatter: (value, offerInfo, { ecpc, currency_code, original_ecpc, original_currency_code }) => (
      <TargetValueFormat
        format={FORMAT_CURRENCY}
        currency={currency_code}
        min={ecpc}
        max={ecpc}
        originalCurrency={original_currency_code}
        originalMin={original_ecpc}
        originalMax={original_ecpc}
      />
    ),
  },
  {
    title: t('table.approval_time'),
    name: 'approval_time',
    format: 'number',
    precision: 0,
    tooltip: t('table.approval_timeTooltip'),
    suffix: t('table.approval_time_suffix')
  },
  {
    title: t('table.cookie_lifetime'),
    name: 'cookie_lifetime',
    format: 'number',
    precision: 0,
    tooltip: t('table.cookie_lifetimeTooltip'),
    suffix: t('table.cookie_lifetime_suffix')
  },
];


const responsiveQuery = {
  dense: {
    maxWidth: 485
  },
  promoMobile: {
    maxWidth: 541
  }
};

export default function OfferCard({
  id,
  linkSource,
  isCoupon = false,
  showActions,
  showTargetsTable,
  manualFetch,
  customActions,
  customBody,
  customTitle,
  onFavoriteClick,
  ...rest
}) {
  const offer = useSelector(state => state.offers[isCoupon ? 'couponsById' : 'offersById'][id] ?? null);
  const onFavClick = useOnFavoriteClick(id, onFavoriteClick);
  const [, offerTagsByCode] = useOfferTags();
  const visibleTagCodes = offer?.tags?.filter(code => offerTagsByCode[code]?.isVisible).sort((code1, code2) => offerTagsByCode[code1]?.id - offerTagsByCode[code2]?.id) ?? [];
  const tagColors = visibleTagCodes.map(code => offerTagsByCode[code]?.offer_bg_color).filter(color => !!color);
  const promoOffersInfo = usePromoOffers();
  const promo = promoOffersInfo?.[id];
  const hasBFTag = !promo && visibleTagCodes?.includes(BLACK_FRIDAY_CODE);
  const hasOtherTag = !promo && visibleTagCodes?.length && !visibleTagCodes?.includes(BLACK_FRIDAY_CODE);
  const theme = useTheme()
  const headerTheme = hasOtherTag || promo || (isCoupon && offer.is_exclusive) ? muiTheme : {}
  const headerBackgroundColor = hasBFTag && theme.palette.mode === 'dark' ? null : promo?.bg_color || tagColors[tagColors.length - 1] || null

  return (
    <ContainerQuery query={responsiveQuery}>
      {params => (
        <Card
          cardActionsClasses={{ root: classes.actions }}
          expanded={false}
          footerText={isCoupon ? null : rest.description}
          className={classNames(classes.offerCard, rest.className, {
            offerCardDense: params.dense,
            offerCardPromoMobile: params.promoMobile,
            [promoClasses.card]: promo,
          })}
          zDepth={0}
          data={offer}
          cardContentClasses={{ root: classes.cardContentClassesRoot }}
          sx={{
            padding: '0px',
            '&:last-child': {
              padding: '0px',
            }
          }}
        >
          <div className={classes.offerCardContent}>
            <div
              className={classNames(
                classes.header,
                {
                  [classes['header--dense']]: rest.denseTop,
                  [promoClasses.cardHeader]: promo,
                })
              }
              style={{
                backgroundColor: headerBackgroundColor,
                '--promo-color': (promo?.bg_color || null),
                '--promo-image': (promo?.bg_image ? `url(${promo.bg_image[0]?.source})` : null),
              }}
            >
              <MuiThemeProvider theme={headerTheme}>
                {customTitle ? customTitle(id) : null}

                <div className={classNames(classes.mainInfo, rest.mainInfoClassName, {
                  [classes['mainInfo--dense']]: params.dense,
                  [promoClasses.mainInfo]: promo,
                })}>
                  <OfferCardAvatar offerId={isCoupon ? offer?.offer : offer?.id} entity={offer} linkSource={linkSource} isCoupon={isCoupon} />
                  <div>
                    <OfferCardTitle entity={offer} plainTitle={isCoupon} linkSource={linkSource} />
                    <OfferCardSubheader offerId={isCoupon ? offer?.offer : offer?.id} entity={offer} linkSource={linkSource} isCoupon={isCoupon} />
                  </div>
                </div>
                <OfferCardHeader offer={offer} manualFetch={manualFetch} />
              </MuiThemeProvider>

              {visibleTagCodes.length ? (
                <div className={classes.tags}>
                  {visibleTagCodes.map(code => {
                    const tag = offerTagsByCode[code]

                    return (
                      <Tooltip key={code} title={tag.translated_name || tag.name}>
                        <OfferCardTag className={classes.tag} color={tag.basic_color} />
                      </Tooltip>
                    )
                  })}
                </div>
              ) : null}
            </div>

            <Divider style={{ clear: 'both' }} />

            <OfferCardOptions offer={offer} isCoupon={isCoupon} />

            {showTargetsTable && (
              <OfferTargetsTable
                dense={params.dense}
                offerId={offer?.id}
              />
            )}

            {isCoupon && (
              <Typography
                className={classes.description}
                color="textPrimary"
              >
                {offer?.description}
              </Typography>
            )}

            {customBody ? customBody(offer?.id) : null}

            {(promo?.has_title && promo?.title) ? <div className={promoClasses.badge}>{promo.translated_title || promo.title}</div> : null}

            {showActions ?
              (
                <OfferCardActions
                  className={classes.offerCardActions}
                  dense={params.dense}
                  offerId={offer?.id}
                  offer={offer}
                  customActions={customActions}
                  isCoupon={isCoupon}
                  onFavClick={onFavClick}
                />
              ) :
              null
            }
          </div>
        </Card>
      )}
    </ContainerQuery>
  )
}

OfferCard.propTypes = {
  id: PropTypes.number.isRequired,
  linkSource: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isCoupon: PropTypes.bool,
  showActions: PropTypes.bool,
  showTargetsTable: PropTypes.bool,
  manualFetch: PropTypes.bool,
  customActions: PropTypes.array,
  customBody: PropTypes.func,
  customTitle: PropTypes.func,
  onFavoriteClick: PropTypes.func,
  cardContentClasses: PropTypes.object,
  description: PropTypes.string,
  denseTop: PropTypes.bool,
}

OfferCard.defaultProps = {
  showActions: true,
  showTargetsTable: true,
  descriptionInTheMiddle: false,
  regions: []
}
